import { z } from 'zod/v4';

const isoDateStringSchema = z.iso.datetime();

export interface Row extends z.infer<typeof RowSchema> {}
export const RowSchema = z.record(z.string(), z.unknown());

export interface Rows extends z.infer<typeof RowsSchema> {}
export const RowsSchema = z.array(RowSchema);

export interface AskUserOption extends z.infer<typeof AskUserOptionSchema> {}
export const AskUserOptionSchema = z.object({
    title: z.string().trim().min(1),
    description: z.string().trim().min(1).optional(),
});

export interface AskUserQuestion extends z.infer<typeof AskUserQuestionSchema> {}
export const AskUserQuestionSchema = z.object({
    question: z.string().trim().min(1),
    options: z.array(AskUserOptionSchema).min(1),
    /** Hide options already chosen for earlier questions, e.g. when a baseline and a comparison must differ. */
    excludeEarlierAnswers: z.boolean().optional(),
});

export interface AskUserMessage extends z.infer<typeof AskUserMessageSchema> {}
export const AskUserMessageSchema = z.object({
    type: z.literal('askUser'),
    questions: z.array(AskUserQuestionSchema).min(1),
});

export interface TodoItem extends z.infer<typeof TodoItemSchema> {}
export const TodoItemSchema = z.object({
    text: z.string().trim().min(1),
    done: z.boolean(),
});

export interface TodoList extends z.infer<typeof TodoListSchema> {}
export const TodoListSchema = z.object({
    items: z.array(TodoItemSchema),
});

export const TodoActionSchema = z.enum(['start', 'read', 'add', 'done']);

export interface TodoMessage extends z.infer<typeof TodoMessageSchema> {}
export const TodoMessageSchema = TodoListSchema.extend({
    type: z.literal('todo'),
    action: TodoActionSchema.optional(),
    tasks: z.array(z.string().trim().min(1)).optional(),
});

export interface DatasetQuerySource extends z.infer<typeof DatasetQuerySourceSchema> {}
export const DatasetQuerySourceSchema = z.object({
    table: z
        .string()
        .trim()
        .min(1)
        .max(240)
        .describe('Exact database-qualified physical table name referenced by the query.'),
    columns: z
        .array(z.string().trim().min(1).max(240))
        .max(200)
        .describe('Exact physical source column names referenced from this table, excluding aliases.'),
});
export const DatasetQuerySourcesSchema = z.array(DatasetQuerySourceSchema).max(50);

export interface Dataset extends z.infer<typeof DatasetSchema> {}
export const DatasetSchema = z.object({
    id: z.string().trim().min(1),
    dimensions: z.array(z.string().trim().min(1)).min(1),
    source: z.array(z.unknown()),
    query: z.string().trim().min(1).optional(),
    dataSources: DatasetQuerySourcesSchema.optional(),
});

export interface DatasetRef extends z.infer<typeof DatasetRefSchema> {}
export const DatasetRefSchema = DatasetSchema.pick({
    id: true,
    dimensions: true,
    dataSources: true,
});

export interface DatasetMessage extends z.infer<typeof DatasetMessageSchema> {}
export const DatasetMessageSchema = DatasetRefSchema.extend({
    type: z.literal('dataset'),
});

export interface ChartDatasetBinding extends z.infer<typeof ChartDatasetBindingSchema> {}
export const ChartDatasetBindingSchema = z.object({
    datasetId: z.string().trim().min(1),
    dimensions: z.array(z.string().trim().min(1)).min(1).optional(),
});

export const VegaLiteDescriptionSchema = z.string().trim().min(1).max(280);

export interface VegaLiteMessage extends z.infer<typeof VegaLiteMessageSchema> {}
export const VegaLiteMessageSchema = z.object({
    type: z.literal('vegaLite'),
    description: VegaLiteDescriptionSchema.optional(),
    spec: z.record(z.string(), z.unknown()),
    datasets: z.array(ChartDatasetBindingSchema).min(1),
});

export interface ChartSavedMessage extends z.infer<typeof ChartSavedMessageSchema> {}
export const ChartSavedMessageSchema = z.object({
    type: z.literal('chartSaved'),
    chartId: z.string().uuid(),
    title: z.string().trim().min(1),
    url: z.string().trim().min(1),
});

export interface ChartsMessage extends z.infer<typeof ChartsMessageSchema> {}
export const ChartsMessageSchema = z.object({
    type: z.literal('charts'),
    charts: z.array(
        z.object({
            id: z.string().uuid(),
            title: z.string().trim().min(1),
            url: z.string().trim().min(1),
            updatedAt: isoDateStringSchema,
            datasets: z.array(ChartDatasetBindingSchema),
        }),
    ),
});

export interface ErrorMessage extends z.infer<typeof ErrorMessageSchema> {}
export const ErrorMessageSchema = z.object({
    type: z.literal('error'),
    code: z.string().trim().min(1),
    message: z.string().trim().min(1),
    retryable: z.boolean().optional(),
    retryCount: z.number().int().nonnegative().optional(),
});
